import { useEffect, useState } from "react"
import { Loader2, Search } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { producaoApi, errorMessage, type SaleOrderHit } from "./api"
import { useProducao } from "./context"
import { fmtEur } from "./lib"

/**
 * «+ Obra a partir de ordem de venda» — enquanto a adjudicação automática não
 * existir, é assim que as obras entram. Pesquisa as ordens confirmadas no Odoo
 * que ainda não têm obra; a obra nasce ligada a ela. Não há obra manual neste
 * diálogo; em modo `host` a rota também a recusa (decisão 3, 07/10).
 */
export function NewJobDialog({ open, onOpenChange }: { open: boolean; onOpenChange(o: boolean): void }) {
  const { shell, upsertJob } = useProducao()
  const [search, setSearch] = useState("")
  const [orders, setOrders] = useState<SaleOrderHit[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState<number | null>(null)

  useEffect(() => {
    if (!open || !shell) return
    let cancelled = false
    setLoading(true)
    const t = setTimeout(() => {
      producaoApi
        .saleOrders(shell, search.trim())
        .then((r) => !cancelled && (setOrders(r), setError(null)))
        .catch((err) => !cancelled && setError(errorMessage(err)))
        .finally(() => !cancelled && setLoading(false))
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [open, shell, search])

  const create = async (so: SaleOrderHit) => {
    if (!shell) return
    setCreating(so.id)
    try {
      const { job, warning } = await producaoApi.fromSaleOrder(shell, so)
      upsertJob(job)
      setOrders((list) => list?.filter((o) => o.id !== so.id) ?? null)
      // Modo `host`: a obra fica gravada mesmo que o contrato falhe (o Detalhe tem «Criar contrato»).
      if (warning) shell.bridge.toast({ variant: "info", title: `Obra criada · ${job.client_name}`, description: warning })
      else shell.bridge.toast({ variant: "success", title: `Obra criada · ${job.client_name}` })
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Não foi possível criar a obra", description: errorMessage(err) })
    } finally {
      setCreating(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Obra a partir de ordem de venda</DialogTitle>
          <p className="text-sm text-muted-foreground">Ordens de venda confirmadas no Odoo que ainda não têm obra.</p>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            className="pl-9"
            placeholder="Nº da ordem, cliente ou referência"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Pesquisar ordens de venda"
          />
        </div>
        <div className="max-h-[50vh] overflow-y-auto">
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : loading && !orders ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : orders && orders.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Nenhuma ordem de venda por converter.</p>
          ) : (
            <ul className="divide-y divide-border">
              {orders?.map((o) => (
                <li key={o.id} className="flex items-center gap-3 py-2.5">
                  <div className="w-20 shrink-0 text-sm font-semibold">{o.name}</div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{o.client.name ?? "—"}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {[o.client.city, o.client_order_ref, fmtEur(o.amount_total)].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <Button size="sm" onClick={() => create(o)} disabled={creating != null}>
                    {creating === o.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Criar obra
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
